import { Download, Smartphone } from "lucide-react"
import { Button } from "../ui/button"
import { motion } from "framer-motion"
import assetQRDeWP from '@/assets/QR_deWP.svg.png'

// Platform Icon Components
const WindowsIcon = ({ className }: { className?: string }) => (
  <img src="/icons/Windows.svg" alt="Windows" className={className} />
)

const MacIcon = ({ className }: { className?: string }) => (
  <img src="/icons/Apple_IOS.svg" alt="macOS" className={className} />
)

const LinuxIcon = ({ className }: { className?: string }) => (
  <img src="/icons/Linux.svg" alt="Linux" className={className} />
)

interface Platform {
  type: "windows" | "mac" | "linux"
  label: string
  version: string
  size: string
  icon: React.ReactNode
  url: string
  color: string
  bg: string
}

const PLATFORMS: Platform[] = [
  {
    type: "windows",
    label: "Windows",
    version: "v3.2.1",
    size: "142 MB",
    icon: <WindowsIcon className="w-8 h-8" />,
    url: "#",
    color: "text-blue-500",
    bg: "bg-blue-500/10",
  },
  {
    type: "mac",
    label: "macOS",
    version: "v3.2.1",
    size: "138 MB",
    icon: <MacIcon className="w-8 h-8" />,
    url: "#",
    color: "text-slate-600 dark:text-slate-300",
    bg: "bg-slate-500/10",
  },
  {
    type: "linux",
    label: "Linux (Debian)",
    version: "v3.2.1",
    size: "124 MB",
    icon: <LinuxIcon className="w-8 h-8" />,
    url: "#",
    color: "text-orange-500",
    bg: "bg-orange-500/10",
  },
]

const MOBILE_QR = [
  {
    label: "Google Play",
    platform: "Android",
    url: "https://play.google.com/store",
    icon: <img src="/icons/Android.svg" alt="Android" className="w-6 h-6" />,
    description: "InMeet for Android",
  },
  {
    label: "App Store",
    platform: "iOS",
    url: "https://apps.apple.com",
    icon: <img src="/icons/Apple_IOS.svg" alt="iOS" className="w-6 h-6" />,
    description: "InMeet for iPhone & iPad",
  },
]

function MiniQR({ value: _value, size = 100 }: { value: string; size?: number }) {
  return (
    <div className="bg-card p-2 rounded-lg">
      <img 
        src={assetQRDeWP} 
        alt="QR Code" 
        width={size} 
        height={size} 
        className="rounded"
      />
    </div>
  )
}

export default function DownloadCenter() {
  const handleDownload = (platform: Platform) => {
    const names: Record<string, string> = {
      windows: "InMeet-Room.exe",
      linux:   "InMeet-Room.deb",
      mac:     "InMeet-Room.dmg",
    }
    const a = document.createElement("a")
    a.href = platform.url
    a.download = names[platform.type]
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  return (
    <div className="flex flex-col h-full min-h-0 gap-5 p-4 overflow-y-auto">
      {/* Header */}
      <div>
        <h2 className="text-base font-bold text-foreground">Download Center</h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Download and install the InMeet Room software on your conference room device
        </p>
      </div>

      {/* Desktop Downloads */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-4">Desktop Applications</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {PLATFORMS.map((platform, index) => (
            <motion.div
              key={platform.type}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="relative bg-card dark:bg-card border border-stroke-100 dark:border-border rounded-2xl p-6 shadow-xs hover:shadow-md transition-all duration-200 overflow-hidden group"
            >
              {/* Decorative Background Wave */}
              <div className={`absolute inset-0 ${platform.bg}`}>
                <svg 
                  className="absolute bottom-0 right-0 w-full h-full" 
                  viewBox="0 0 200 120" 
                  fill="none" 
                  preserveAspectRatio="none"
                >
                  <path 
                    d="M200 120V80C180 70 160 75 140 80C120 85 100 90 80 85C60 80 40 70 20 75C10 77.5 5 79 0 80V120H200Z" 
                    fill="currentColor" 
                    className="text-white/30 dark:text-white/10"
                  />
                </svg>
              </div>

              {/* Content */}
              <div className="relative z-10 flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-2xl bg-card/50 dark:bg-card/10 flex items-center justify-center mb-4 shadow-sm">
                  <span className={platform.color}>{platform.icon}</span>
                </div>
                <h4 className="text-base font-bold text-foreground mb-1">{platform.label}</h4>
                <p className="text-xs text-muted-foreground mb-4">{platform.version} · {platform.size}</p>
                <Button
                  className="w-full gap-2 group-hover:scale-105 transition-transform"
                  size="sm"
                  onClick={() => handleDownload(platform)}
                >
                  <Download className="w-4 h-4" />
                  Download
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Mobile Downloads with QR */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-4">Mobile Applications</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {MOBILE_QR.map((app, index) => (
            <motion.div
              key={app.platform}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (PLATFORMS.length + index) * 0.1, duration: 0.5 }}
              className="relative bg-card dark:bg-card border border-stroke-100 dark:border-border rounded-2xl p-5 shadow-xs hover:shadow-md transition-all duration-200 overflow-hidden"
            >
              {/* Decorative Background Wave */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary/10">
                <svg 
                  className="absolute bottom-0 right-0 w-full h-full" 
                  viewBox="0 0 200 120" 
                  fill="none" 
                  preserveAspectRatio="none"
                >
                  <path 
                    d="M200 120V80C180 70 160 75 140 80C120 85 100 90 80 85C60 80 40 70 20 75C10 77.5 5 79 0 80V120H200Z" 
                    fill="currentColor" 
                    className="text-white/30 dark:text-white/10"
                  />
                </svg>
              </div>

              {/* Content */}
              <div className="relative z-10 flex items-center gap-5">
                <div className="bg-card p-3 rounded-xl shadow-sm border border-gray-200">
                  <MiniQR value={app.url} size={80} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {app.icon}
                    <h4 className="text-base font-bold text-foreground">{app.label}</h4>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{app.description}</p>
                  <div className="flex items-center gap-1.5">
                    <Smartphone className="w-4 h-4 text-primary" />
                    <span className="text-xs font-medium text-primary">Scan QR to download</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Setup Instructions */}
      <div className="bg-primary/5 rounded-2xl border border-primary/20 p-5">
        <h3 className="text-sm font-semibold text-foreground mb-3">Quick Setup Guide</h3>
        <ol className="space-y-2">
          {[
            "Download and install InMeet Room for your platform",
            "Launch the app and select \"Pair with InMeet\"",
            "Navigate to Rooms → Manage Rooms and click any room to view its Activation Key",
            "Enter the Activation Key and Password in the app, or scan the QR code",
            "Your room device is now paired and ready to start meetings",
          ].map((step, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="w-5 h-5 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                {i + 1}
              </span>
              <span className="text-xs text-foreground/80">{step}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  )
}
